import React, { useState, useEffect } from "react";

const slugify = (s = "") =>
  String(s)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const imgUrl = (API, img) => {
  if (!img) return null;
  if (img.startsWith("http")) return img;
  if (img.startsWith("/")) return img;
  if (img.startsWith("uploads/")) return `${API}/${img}`;
  return `${API}/uploads/${img}`;
};

const EMPTY = { name: "", slug: "", sortOrder: "", isActive: true };

/**
 * Category Management — every category is an independent MAIN category.
 * Props:
 *   API         backend base URL
 *   pass        admin password (same one the dashboard already holds)
 *   categories  list from GET /api/admin/categories (includes productCount)
 *   onChanged   call after any change so the dashboard reloads the list
 */
export default function CategoryManager({ API, pass, categories, onChanged }) {
  const [editingId, setEditingId] = useState(null); // null | "new" | category _id
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const openNew = () => {
    setEditingId("new");
    setForm(EMPTY);
    setSlugTouched(false);
    setFile(null);
  };

  const openEdit = (c) => {
    setEditingId(c._id);
    setForm({ name: c.name, slug: c.slug, sortOrder: c.sortOrder ?? "", isActive: c.isActive });
    setSlugTouched(true);
    setFile(null);
  };

  const close = () => { setEditingId(null); setFile(null); };

  const save = async () => {
    if (!form.name.trim()) { flash("error", "Category name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("password", pass);
      fd.append("name", form.name.trim());
      fd.append("slug", (form.slug || slugify(form.name)).trim());
      fd.append("sortOrder", form.sortOrder === "" ? "" : String(form.sortOrder));
      fd.append("isActive", String(form.isActive));
      if (file) fd.append("image", file);

      const isNew = editingId === "new";
      const res = await fetch(
        isNew ? `${API}/api/admin/categories` : `${API}/api/admin/categories/${editingId}`,
        { method: isNew ? "POST" : "PUT", body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { flash("error", data.error || "Could not save category"); setSaving(false); return; }

      flash("success", isNew ? "Category added" : "Category updated");
      close();
      onChanged && onChanged();
    } catch {
      flash("error", "Network error — could not save category");
    }
    setSaving(false);
  };

  const toggleActive = async (c) => {
    setBusyId(c._id);
    try {
      const fd = new FormData();
      fd.append("password", pass);
      fd.append("isActive", String(!c.isActive));
      const res = await fetch(`${API}/api/admin/categories/${c._id}`, { method: "PUT", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) flash("error", data.error || "Could not update status");
      else onChanged && onChanged();
    } catch {
      flash("error", "Network error");
    }
    setBusyId(null);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    setBusyId(c._id);
    try {
      const res = await fetch(
        `${API}/api/admin/categories/${c._id}?password=${encodeURIComponent(pass)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) flash("error", data.error || "Could not delete category");
      else { flash("success", "Category deleted"); onChanged && onChanged(); }
    } catch {
      flash("error", "Network error");
    }
    setBusyId(null);
  };

  const editingCat = categories.find((c) => c._id === editingId);
  const currentImage = preview || imgUrl(API, editingCat?.image);

  return (
    <div style={st.panel}>
      <style>{`
        .cm-card:hover{border-color:rgba(200,160,74,0.45)!important}
        .cm-btn:hover{opacity:.85}
      `}</style>

      <div style={st.head}>
        <div style={{ fontSize: 13, color: "#9a7050", fontFamily: "'DM Sans',sans-serif" }}>
          {categories.length} categor{categories.length === 1 ? "y" : "ies"} ·{" "}
          {categories.filter((c) => c.isActive).length} active
        </div>
        <button
          className="cm-btn"
          onClick={editingId === "new" ? close : openNew}
          style={{
            background: editingId === "new" ? "rgba(198,40,40,0.15)" : "#c8a04a",
            color: editingId === "new" ? "#f87171" : "#1a1008",
            border: editingId === "new" ? "1px solid rgba(198,40,40,0.3)" : "none",
            padding: "9px 20px", fontSize: 12, fontWeight: 600, borderRadius: 8,
            cursor: "pointer", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.06em",
          }}
        >
          {editingId === "new" ? "✕ Cancel" : "+ Add Category"}
        </button>
      </div>

      {msg.text && (
        <div style={{
          marginBottom: 16, padding: "10px 16px", borderRadius: 8, fontSize: 13,
          background: msg.type === "error" ? "rgba(198,40,40,0.1)" : "rgba(74,222,128,0.1)",
          border: `1px solid ${msg.type === "error" ? "rgba(198,40,40,0.3)" : "rgba(74,222,128,0.3)"}`,
          color: msg.type === "error" ? "#f87171" : "#4ade80",
        }}>
          {msg.type === "error" ? "⚠️ " : "✅ "}{msg.text}
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {editingId && (
        <div style={st.form}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, color: "#e8d5a3", margin: "0 0 18px" }}>
            {editingId === "new" ? "New Category" : `Edit — ${editingCat?.name || ""}`}
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
            <div>
              <label style={st.label}>Category Name *</label>
              <input
                value={form.name}
                placeholder="e.g. Puff Sleeves – Front Hook"
                onChange={(e) => {
                  const name = e.target.value;
                  setForm({ ...form, name, slug: slugTouched ? form.slug : slugify(name) });
                }}
                style={st.input}
              />
            </div>
            <div>
              <label style={st.label}>Slug (URL)</label>
              <input
                value={form.slug}
                placeholder="auto-generated"
                onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }}
                style={{ ...st.input, fontFamily: "monospace" }}
              />
            </div>
            <div>
              <label style={st.label}>Sort Order</label>
              <input
                type="number"
                value={form.sortOrder}
                placeholder="blank = add at end"
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                style={st.input}
              />
            </div>
            <div>
              <label style={st.label}>Status</label>
              <button
                type="button"
                onClick={() => setForm({ ...form, isActive: !form.isActive })}
                style={{
                  width: "100%", padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600,
                  background: form.isActive ? "rgba(74,222,128,0.1)" : "rgba(198,40,40,0.1)",
                  color: form.isActive ? "#4ade80" : "#f87171",
                  border: `1px solid ${form.isActive ? "rgba(74,222,128,0.3)" : "rgba(198,40,40,0.3)"}`,
                }}
              >
                {form.isActive ? "● Active (shown on store)" : "○ Inactive (hidden)"}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={st.label}>Category Image</label>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{
                width: 84, height: 84, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                border: "2px solid rgba(200,160,74,0.35)", background: "#0f0a04",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {currentImage
                  ? <img src={currentImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 28, opacity: 0.4 }}>🖼️</span>}
              </div>
              <label style={{
                padding: "9px 16px", border: "1px dashed rgba(200,160,74,0.4)", borderRadius: 8,
                color: "#c8a04a", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              }}>
                {currentImage ? "Change image" : "Upload image"}
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => { setFile(e.target.files[0] || null); e.target.value = ""; }} />
              </label>
              <span style={{ fontSize: 11, color: "#7a5a30" }}>Shown in a circle — a square photo works best.</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="cm-btn" onClick={save} disabled={saving}
              style={{ background: saving ? "#5a3a10" : "#c8a04a", color: "#1a1008", border: "none", padding: "10px 24px", fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving…" : "Save Category"}
            </button>
            <button className="cm-btn" onClick={close}
              style={{ background: "transparent", color: "#9a7050", border: "1px solid rgba(200,160,74,0.2)", padding: "10px 20px", fontSize: 13, borderRadius: 8, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CATEGORY LIST */}
      {categories.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#5a3a10", fontFamily: "'DM Sans',sans-serif", fontSize: 14 }}>
          No categories yet. Click “+ Add Category”.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 14 }}>
          {categories.map((c) => {
            const img = imgUrl(API, c.image);
            const busy = busyId === c._id;
            return (
              <div key={c._id} className="cm-card" style={{ ...st.card, opacity: c.isActive ? 1 : 0.65 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                    border: "2px solid rgba(200,160,74,0.3)", background: "#0f0a04",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {img
                      ? <img src={img} alt={c.name} onError={(e) => { e.target.style.display = "none"; }} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: 22, opacity: 0.4 }}>🥻</span>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, color: "#e8d5a3", fontWeight: 600, lineHeight: 1.3 }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: "#7a5a30", fontFamily: "monospace", marginTop: 2, wordBreak: "break-all" }}>/{c.slug}</div>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: c.productCount === 0 ? "#5a3a10" : "#c8a04a", fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>
                    {c.productCount} product{c.productCount !== 1 ? "s" : ""}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 12,
                    background: c.isActive ? "rgba(74,222,128,0.12)" : "rgba(198,40,40,0.12)",
                    color: c.isActive ? "#4ade80" : "#f87171",
                  }}>
                    {c.isActive ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  <button className="cm-btn" disabled={busy} onClick={() => openEdit(c)}
                    style={{ flex: 1, padding: "7px 0", background: "rgba(200,160,74,0.15)", color: "#c8a04a", border: "1px solid rgba(200,160,74,0.25)", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    ✏️ Edit
                  </button>
                  <button className="cm-btn" disabled={busy} onClick={() => toggleActive(c)}
                    style={{ flex: 1, padding: "7px 0", background: "transparent", color: "#9a7050", border: "1px solid rgba(200,160,74,0.2)", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    {c.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button className="cm-btn" disabled={busy} onClick={() => remove(c)}
                    style={{ padding: "7px 10px", background: "rgba(198,40,40,0.1)", color: "#f87171", border: "1px solid rgba(198,40,40,0.2)", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const st = {
  panel: { background: "#140d04", border: "1px solid rgba(200,160,74,0.1)", borderRadius: 12, padding: "24px" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 },
  form: { background: "#1a1008", border: "1px solid rgba(200,160,74,0.2)", borderRadius: 10, padding: "24px", marginBottom: 24 },
  card: { background: "#1a1008", border: "1px solid rgba(200,160,74,0.12)", borderRadius: 10, padding: "16px", transition: "border-color .2s" },
  label: { display: "block", fontSize: 10, fontWeight: 600, color: "#7a5a30", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5, fontFamily: "'DM Sans',sans-serif" },
  input: { width: "100%", padding: "9px 12px", fontSize: 13, background: "#0f0a04", border: "1px solid rgba(200,160,74,0.2)", borderRadius: 6, color: "#e8d5a3", outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box" },
};